// frontendNgeo/src/pages/Dashboard.js
import React, { useEffect, lazy, useState, useMemo, useCallback, Suspense } from "react";
import axios from "axios";
import "leaflet/dist/leaflet.css";
import { useAuth } from "../context/AuthContext";
import { useNucleoFilters } from "../hooks/useNucleoFilters";
import { normalizeString } from "../utils/string";
import { useAtividadesGeo } from '../hooks/useAtividadesGeo';
import useNucleosData from "../components/useNucleosData";

import MapComponent from "../components/MapComponent";
import FilterControls from "../components/FilterControls";

import ViewSwitcher from '../components/ViewSwitcher';
import PainelPrincipal from '../components/PainelPrincipal';
import PainelCasaLegal from '../components/PainelCasaLegal';
import PainelAtividadesNgeo from '../components/PainelAtividadesNgeo';
import PainelNgeoInfo from '../components/PainelNgeoInfo';
import PainelCerurb from '../components/PainelCerurb';

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js";


import '../App.css';

ChartJS.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale);


const ChartsPainelPrincipal = lazy(() => import('../components/charts/ChartsPainelPrincipal.jsx'));
const ChartsCasaLegalPlanilha = lazy(() => import('../components/charts/ChartsCasaLegalPlanilha.jsx'));
const ChartsContainer = lazy(() => import('../components/charts/ChartsContainer.jsx'));
const ChartsContainerAtividades = lazy(() => import('../components/charts/ChartsContainerAtividades.jsx'));
const ChartsNgeoInfo = lazy(() => import('../components/charts/ChartsNgeoInfo.jsx'));


function Dashboard() {
  const {  user, logout } = useAuth();
  const username = user?.username || user?.nome;
  const [selectedTerritorio, setSelectedTerritorio] = useState("Todos");
  const [selectedMunicipio, setSelectedMunicipio] = useState("Todos");
  const [selectedNucleo, setSelectedNucleo] = useState("Todos");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [showCharts, setShowCharts] = useState(true);
  const [activeView, setActiveView] = useState('painelPrincipal');
  const [mapKey, setMapKey] = useState(0);




  const handleBuscarCerurbDados = () => { /* ... */ };
  const handleBuscarNGEODados = () => { /* ... */ };

  const handleMapFeatureClick = (data) => {
    if (data.tipo === 'TERRITORIO') {
      // Se clicar no território já selecionado, reseta tudo
      if (data.territorio === selectedTerritorio) {
        setSelectedTerritorio("Todos");
        setSelectedMunicipio("Todos");
        setSelectedNucleo("Todos");
      } else {
        setSelectedTerritorio(data.territorio);
        setSelectedMunicipio("Todos");
        setSelectedNucleo("Todos");
      }
    } 
    else if (data.tipo === 'MUNICIPIO') {
      if (data.municipio === selectedMunicipio) {
        setSelectedMunicipio("Todos");
        setSelectedNucleo("Todos");
      } else {
        setSelectedTerritorio(data.territorio || "Todos");
        setSelectedMunicipio(data.municipio);
        setSelectedNucleo("Todos"); // Ao trocar de cidade, reseta o núcleo
      }
    } 
    else if (data.tipo === 'NUCLEO') {
      if (data.nucleo === selectedNucleo) {
        setSelectedNucleo("Todos");
      } else {
        setSelectedTerritorio(data.territorio || "Todos");
        setSelectedMunicipio(data.municipio || "Todos");
        setSelectedNucleo(data.nucleo);
      }
    }
  };

  

  // Reseta o núcleo ao trocar de painel
  useEffect(() => {
    setSelectedNucleo("Todos");
  }, [activeView]);

  // Reseta filtro de status ao mudar município, núcleo ou painel
  useEffect(() => {
    setSelectedStatus(null);
  }, [selectedMunicipio, selectedNucleo, activeView]);

  // Reseta seleção de núcleo ao entrar no painel principal (visão geral)
  useEffect(() => {
    if (activeView === 'painelPrincipal') {
      setSelectedNucleo("Todos");
      setSelectedMunicipio("Todos");
    }
  }, [activeView]);

  const handleStatusSelect = useCallback((status) => {
    setSelectedStatus(prev => prev === status ? null : status);
  }, []);

  const { nucleosExternos, loading: isLoadingNucleosExternos } = useNucleosData(
    normalizeString(selectedMunicipio),
    normalizeString(selectedNucleo)
  );
  const { atividadesData, loading: loadingPainel2, error } = useAtividadesGeo(selectedTerritorio, selectedMunicipio);


  const [layersVisibility, setLayersVisibility] = useState({
    lotes: true,
    quadras: true,
    nucleos: true,
    municipios: true,
    territorios: true
  });


  const {
    lotes,
    quadras,
    nucleosFiltrados,
    municipiosMap,
    nucleos,
    territorios,
    territoriosOptions,
    municipiosFiltrados,
    municipiosComNucleos,
    municipioToTerritorio,
    nucleoToMunicipioMap,
    nucleoGidMap,
    statusCounts,
    filteredBounds,
    nucleosExternosFiltrados,
    dadoscontroleFiltrados,
    loading: loadingPainel1,
    loadingDetails,
    resumoPlanilha, 
    planilhaData,
    estatisticasPlanilha,
    territorioStatsFiltrado,
    estatisticasPorMesFiltrado,
  } = useNucleoFilters(selectedTerritorio, selectedMunicipio, selectedNucleo, nucleosExternos, activeView, selectedStatus);

  
  const handleMunicipioChange = useCallback((municipio) => {
    setSelectedMunicipio(municipio);
    setSelectedNucleo("Todos");
    if (municipio !== "Todos" && municipioToTerritorio[municipio]) {
      setSelectedTerritorio(municipioToTerritorio[municipio]);
    }
  }, [municipioToTerritorio]);

  const handleNucleoChange = useCallback((nucleo) => {
    setSelectedNucleo(nucleo);
    if (nucleo !== "Todos") {
      const info = nucleoToMunicipioMap[nucleo];
      if (info?.municipio) setSelectedMunicipio(info.municipio);
      if (info?.territorio) setSelectedTerritorio(info.territorio);
    }
  }, [nucleoToMunicipioMap]);

  const isAppLoading = loadingPainel1;

  const totalVetorizados = useMemo(() => {
    return nucleosExternosFiltrados.reduce((acc, n) => acc + (parseInt(n.totalVetorizados) || 0), 0);
  }, [nucleosExternosFiltrados]);

  const handleSaveControleRow = async (updatedRow) => {
    try {
        const API_URL = process.env.REACT_APP_API_URL;
        const API_KEY = process.env.REACT_APP_API_KEY;

        // Envia para o Backend
        await axios.put(`${API_URL}/dados/planilha-controle/${updatedRow.id}`, updatedRow, {
            headers: { Authorization: `Bearer ${API_KEY}` }
        });

        alert("Linha atualizada com sucesso!");
        
        // Opcional: Recarregar a página para ver os dados atualizados
        // window.location.reload(); 
        
    } catch (error) {
        console.error("Erro ao salvar:", error);
        alert("Erro ao salvar alterações no servidor.");
    }
  };

  const handleSavePlanilhaRow = async (updatedRow) => {
    try {
        const API_URL = process.env.REACT_APP_API_URL;
        const API_KEY = process.env.REACT_APP_API_KEY;

        if (!updatedRow.id) {
             alert("Erro: ID da linha não encontrado. (Planilha casalegal)");
             return;
        }

        // Envia para o novo endpoint que criamos (/api/planilha-cerurb/:id)
        await axios.put(`${API_URL}/dados/planilha-casalegal/${updatedRow.id}`, updatedRow, {
            headers: { Authorization: `Bearer ${API_KEY}` }
        });

        alert("Sucesso! Linha atualizada na Planilha CERURB.");
        
    } catch (error) {
        console.error("Erro ao salvar Planilha CERURB:", error);
        if (error.response) {
             alert(`Erro do Servidor: ${error.response.data.message || "Falha desconhecida"}`);
        } else {
             alert("Erro de conexão ao salvar.");
        }
    }
  };


  return (
    <>
    <div className="dashboard" style={{
        display: "flex",
        height: "100vh",
        width: "100vw",
        background: "#f5f5f5"
      }}>
      {/* Sidebar (filtros + cards) */}
      <div style={{
        backgroundColor: "#f5f5f5",
        padding: "10px",
        overflow: "hidden",
        height: "100vh",
        display: "flex",
        gap: "10px",
        borderRight: "1px solid #ddd"
      }}>
        <div style={{ display: "flex", flexDirection: "column", maxWidth: "600px", height: "100%", overflow: "hidden" }}>
          <FilterControls
            onBuscarResumoSPI={handleBuscarCerurbDados}
            onBuscarNGEODados={handleBuscarNGEODados}
            username={username}
            userRole={user?.role}
            handleLogout={logout}

            selectedTerritorio={selectedTerritorio}
            setSelectedTerritorio={setSelectedTerritorio}
            territoriosOptions={territoriosOptions}

            selectedMunicipio={selectedMunicipio}
            setSelectedMunicipio={handleMunicipioChange}
            municipiosFiltrados={municipiosFiltrados}
            municipiosComNucleos={municipiosComNucleos}
            selectedNucleo={selectedNucleo}
            setSelectedNucleo={handleNucleoChange}
            nucleoGidMap={nucleoGidMap}

            nucleos={nucleos} // <-- Lista para Painel 1
            nucleosExternos={nucleosExternos} 
            activeView={activeView}


            layersVisibility={layersVisibility}
            setLayersVisibility={setLayersVisibility}
            lotes={lotes}
            quadras={quadras}
            municipios={municipiosMap}
            nucleosFiltrados={nucleosFiltrados}
            territorios={territorios}
          />
          
          <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: 1, overflowY: "auto", minHeight: 0 }}>
            <div style={{
              width: "500px",
              background: "#fff",
              padding: 4,
              marginTop: 12,
              boxShadow: "0 2px 6px rgba(0,0,0,0.1)"
            }}>
              {isAppLoading  ? (
                <p style={{ fontSize: 14, color: "#888", height: "400px" }}>Carregando dados...</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                

                  <ViewSwitcher activeView={activeView} setActiveView={setActiveView} />

                  {/*  RENDERIZAÇÃO CONDICIONAL PARA OS PAINÉIS */}
                  {activeView === 'painelPrincipal' && (
                    <PainelPrincipal
                      loading={loadingPainel1}
                      nucleosFiltrados={nucleosFiltrados}
                    />
                  )}

                  {activeView === 'painel1' && (
                    <PainelCasaLegal
                      loading={loadingPainel1}
                      resumoPlanilha={resumoPlanilha}
                      planilhaData={planilhaData}
                      statusCounts={statusCounts}
                      territorioStats={territorioStatsFiltrado}
                      data={atividadesData}
                      error={error}
                      estatisticasPlanilha={estatisticasPlanilha}
                      totalVetorizados={totalVetorizados}
                      onSaveRow={handleSavePlanilhaRow}
                    />
                  )}

                  {activeView === 'painel2' && (
                      <PainelCerurb
                          loading={loadingPainel2}
                          error={error}
                          statusCounts={statusCounts}
                          totalVetorizados={totalVetorizados}
                          nucleosExternosFiltrados={nucleosExternosFiltrados}
                          selectedStatus={selectedStatus}
                          onStatusSelect={handleStatusSelect}
                      />
                  )}

                  {activeView === 'painel3' && (
                      <PainelAtividadesNgeo 
                          data={atividadesData}
                          loading={loadingPainel2}
                          error={error}
                      />
                  )}
                  {activeView === 'painel4' && (
                      <PainelNgeoInfo 
                          data={dadoscontroleFiltrados}
                          error={error}
                          onSaveRow={handleSaveControleRow}
                      />
                  )}

                </div>
              )}
            </div>
          </div>
        
        </div>
          
        <div style={{
          width: showCharts ? 700 : 20,
          height: "100vh",
          overflow: "hidden",
          transition: "width 0.3s ease",
          display: "flex",
          flexDirection: "row",
        }}>
          <div style={{  display: "flex", alignItems: "center", justifyContent: "center" }}>
          <button
              onClick={() => {
                setShowCharts((prev) => !prev);
                setTimeout(() => {
                  setMapKey((prev) => prev + 1); // força recriação do mapa
                }, 300); 
              }}
              style={{
                display: "flex",
                margin: 8,
                background: "#1976d2",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                fontSize: 12,
                cursor: "pointer",
                alignItems: "center",
                height: 32
                
              }}
          >

            {showCharts ? "<" : ">"}
          </button>
          </div>

          

      {showCharts && (
        <div style={{ flex: 1, minWidth: 0, overflowX: "hidden", overflowY: "auto" }}>
          <label style={{ fontWeight: "bold", fontSize: "14px", margin: "5px 0" }}>
            Gráficos
          </label>
          
          {/* O Suspense gerencia o estado de carregamento do lazy */}
          <Suspense fallback={<div style={{ padding: 20 }}>Carregando Gráficos...</div>}>
            
            {activeView === 'painelPrincipal' && (
              <div style={{ width: "100%" }}>
                <ChartsPainelPrincipal
                  nucleosFiltrados={nucleosFiltrados}
                  loading={loadingPainel1}
                />
              </div>
            )}

            {activeView === 'painel1' && (
              <div style={{ width: "100%" }}>
                <ChartsCasaLegalPlanilha
                  statusCounts={statusCounts}
                  nucleosExternos={nucleosExternosFiltrados}
                  municipios={municipiosMap}
                  selectedMunicipio={selectedMunicipio}
                  loading={loadingPainel1}
                  resumoPlanilha={resumoPlanilha}
                  territorioStatsFiltrado={territorioStatsFiltrado}
                  estatisticasPorMesFiltrado={estatisticasPorMesFiltrado}
                  planilhaData={planilhaData}
                />
              </div>
            )}

            {activeView === 'painel2' && (
              <div style={{ width: "100%", margin: "5px" }}>
                <ChartsContainer
                  statusCounts={statusCounts}
                  nucleosExternos={nucleosExternosFiltrados}
                  municipios={municipiosMap}
                  selectedMunicipio={selectedMunicipio}
                  loading={loadingPainel1}
                />
              </div>
            )}

            {activeView === 'painel3' && (
              <div style={{ width: "100%", margin: "5px" }}>
                <ChartsContainerAtividades data={atividadesData} />
              </div>
            )}

            {activeView === 'painel4' && (
              <div style={{ width: "100%" }}>
                <ChartsNgeoInfo data={dadoscontroleFiltrados} />
              </div>
            )}
            
          </Suspense>
        </div>
      )}
        </div>
      </div>

      {/* Mapa */}
      <div style={{ display: "flex", flex: 1, 
            width: "100%",
            padding: 20,
            borderRadius: 8,
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)" }}>
        <MapComponent
          key={`map-main-${mapKey}`}
          lotes={lotes}
          nucleos={nucleosFiltrados}
          quadras={quadras}
          territorios={territorios}
          municipios={municipiosMap}
          bounds={filteredBounds}
          nucleosExternos={nucleosExternosFiltrados}
          layers={layersVisibility}
          onFeatureSelect={handleMapFeatureClick}
          selectedTerritorio={selectedTerritorio}
          selectedMunicipio={selectedMunicipio}
          selectedNucleo={selectedNucleo}
          loadingDetails={loadingDetails && selectedNucleo !== "Todos"}
          activeView={activeView}
        />
      </div>
    </div>
    </>
  );
}

export default Dashboard;
